import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { Tooltip } from '@/components/core/Tooltip';
import { Uploader, type UploadItem } from '@/components/core/Uploader';
import { formatAcceptedFileTypes, formatBytes, validateFile } from '@/components/core/Uploader/utils';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';

import {
    type BulkSubscriptionImportAction,
    deleteUpload,
    useDeleteUpload,
    useImportStagedBulkSubscriptionFile,
    useStageBulkSubscriptionFile,
} from '../bulk-subscriptions.queries';
import { BulkSubscription } from '../bulk-subscriptions.types';

// Server-side allow-list for POST /bulkSubscription/{id}/import/{uploadId} lives in
// BulkSubscriptionResource.java and accepts JSON, XLSX, and XLSM (Figma
// Frame 01 helper text).
const ACCEPTED_FILE_TYPES = ['.json', '.xlsx', '.xlsm'];
// App Engine Standard caps multipart request bodies at 32MB; the Figma
// helper text mirrors that limit. We enforce the same cap client-side so
// users get an inline error instead of a server-side rejection.
const MAX_FILE_SIZE = 32 * 1024 * 1024;

// Map the three Figma radio options to the server's `action` query param
// (see `BulkSubscriptionResource.uploadBulkSubscriptionEntriesForId`).
type ImportOption = {
    action: BulkSubscriptionImportAction;
    labelKey: string;
    tooltipKey: string;
};

const IMPORT_OPTIONS: ImportOption[] = [
    {
        action: 'ADD_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_ADD',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_ADD_HELP',
    },
    {
        action: 'REPLACE_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_REPLACE',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_REPLACE_HELP',
    },
    {
        action: 'DELETE_ENTRIES',
        labelKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_DELETE',
        tooltipKey: 'BULK_SUBSCRIPTION.IMPORT_OPTION_DELETE_HELP',
    },
];

const DEFAULT_ACTION: BulkSubscriptionImportAction = 'ADD_ENTRIES';

interface UploadEntriesDialogProps {
    entity: BulkSubscription,
    open: boolean;
    onClose: (shouldRefetchData?: boolean) => void;
}

const UploadNewEntriesDialog = ({
    entity,
    open,
    onClose,
}: UploadEntriesDialogProps) => {

    const { toast } = useToast();
    const { t } = useTranslation();

    // The dialog runs a genuine two-step flow:
    //   1. Browse / drag & drop → stage the file via `POST /uploads` and a
    //      PUT to the returned `uploadUrl`. Progress bar reflects the real PUT.
    //   2. "Start import" click → hand the staged `uploadId` to
    //      `POST /bulkSubscription/{id}/import/{uploadId}`.
    // `stagedUploadId` bridges the two steps. `uploadItem` drives the
    // Uploader UI. An `AbortController` lets Cancel / Remove / unmount
    // stop an in-flight upload immediately instead of leaving it to
    // race back with stale state.
    const [stagedFile, setStagedFile] = useState<File | undefined>(undefined);
    const [stagedUploadId, setStagedUploadId] = useState<number | undefined>(undefined);
    const [uploadItem, setUploadItem] = useState<UploadItem | undefined>(undefined);
    const [dropFieldError, setDropFieldError] = useState<string | undefined>(undefined);
    const [action, setAction] = useState<BulkSubscriptionImportAction>(DEFAULT_ACTION);

    const stageAbortRef = useRef<AbortController | null>(null);
    // Captured as soon as `POST /uploads` resolves, before the follow-up PUT
    // starts. Every path that discards the current staging (abort, new file,
    // dialog unmount, remove, cancel) reads this ref and DELETEs the row so
    // an aborted PUT does not leak an orphaned Upload + placeholder DigitalAsset.
    // Cleared once the id is either promoted into `stagedUploadId` (PUT
    // succeeded) or the DELETE has been issued.
    const pendingUploadIdRef = useRef<number | undefined>(undefined);

    const { mutate: deleteStagedUpload } = useDeleteUpload();

    const discardPendingUploadRef = useCallback(() => {
        const pending = pendingUploadIdRef.current;
        pendingUploadIdRef.current = undefined;
        if (pending !== undefined) {
            deleteStagedUpload(pending);
        }
    }, [deleteStagedUpload]);

    const abortInFlightStaging = useCallback(() => {
        if (stageAbortRef.current) {
            stageAbortRef.current.abort();
            stageAbortRef.current = null;
        }
        discardPendingUploadRef();
    }, [discardPendingUploadRef]);

    // Dialog unmount: abort the PUT and fire-and-forget DELETE any Upload the
    // POST already created. Calls `deleteUpload` directly instead of the
    // mutation hook, which is being torn down with the component.
    useEffect(() => () => {
        if (stageAbortRef.current) {
            stageAbortRef.current.abort();
            stageAbortRef.current = null;
        }
        const pending = pendingUploadIdRef.current;
        pendingUploadIdRef.current = undefined;
        if (pending !== undefined) {
            // Fire-and-forget; nothing to await on unmount.
            void deleteUpload(pending).catch(() => {
                // Best-effort cleanup: the server-side stale-upload purge is
                // the safety net if this ever fails.
            });
        }
    }, []);

    const {
        mutate: stageFile,
        isPending: isStaging,
    } = useStageBulkSubscriptionFile({
        onSuccess: ({ uploadId }) => {
            stageAbortRef.current = null;
            // Promote the pending id to a fully-staged id; no orphan to clean.
            pendingUploadIdRef.current = undefined;
            setStagedUploadId(uploadId);
            setUploadItem((prev) =>
                prev ? { ...prev, status: 'success', progress: 100 } : prev
            );
        },
        onError: (error) => {
            stageAbortRef.current = null;
            // The PUT never completed. Whether that is an abort (Cancel /
            // Remove / new file / unmount) or a genuine failure, the Upload
            // row the POST created is orphaned and must be DELETE'd.
            discardPendingUploadRef();
            // A user-triggered abort surfaces as an axios CanceledError. It
            // isn't an upload failure — we've already reset the dialog
            // state elsewhere (Cancel / Remove / new file), so swallow it
            // instead of flipping the row into 'error'.
            if (axios.isCancel(error)) {
                return;
            }
            logger.error('UploadNewEntriesDialog: Could not stage file', error);
            const errorMessage = resolveUploadErrorMessage(error, t);
            setUploadItem((prev) =>
                prev
                    ? { ...prev, status: 'error', errorMessage }
                    : prev
            );
        },
    });

    const {
        mutate: importStagedFile,
        isPending: isImporting,
    } = useImportStagedBulkSubscriptionFile({
        onSuccess: () => {
            toast.success('BULK_SUBSCRIPTION.NEW_ENTRIES_UPLOADED_SUCCESSFULLY');
            onClose(true);
        },
        onError: (error) => {
            logger.error('UploadNewEntriesDialog: Could not import staged file', error);
            const errorMessage = resolveUploadErrorMessage(error, t);
            setUploadItem((prev) =>
                prev
                    ? { ...prev, status: 'error', errorMessage }
                    : prev
            );
        },
    });

    const runStaging = useCallback((file: File) => {
        abortInFlightStaging();
        const controller = new AbortController();
        stageAbortRef.current = controller;
        setStagedUploadId(undefined);
        setUploadItem({
            id: file.name,
            file,
            status: 'uploading',
            progress: 0,
            startedAt: Date.now(),
        });
        stageFile({
            file,
            signal: controller.signal,
            onCreated: (uploadId) => {
                // POST /uploads has returned; the row exists but the PUT is
                // still in flight. Record it so any abort / error between now
                // and onSuccess can DELETE it.
                pendingUploadIdRef.current = uploadId;
            },
            onProgress: (percent) => {
                setUploadItem((prev) =>
                    prev && prev.status === 'uploading'
                        ? { ...prev, progress: percent }
                        : prev
                );
            },
        });
    }, [abortInFlightStaging, stageFile]);

    const handleFilesAdded = useCallback((files: File[]) => {
        const [file] = files;
        if (!file) {
            return;
        }
        // Figma Frame 03/02: only one file can be staged at a time. If the
        // user tries to add another file (via drag & drop or via "Browse
        // files") while one is already staged, we must surface an inline
        // error and keep the existing file — silently swapping would risk
        // the user thinking their previous selection is still in play.
        if (stagedFile) {
            setDropFieldError(t('BULK_SUBSCRIPTION.SINGLE_FILE_ONLY_ERROR'));
            return;
        }
        setDropFieldError(undefined);
        setStagedFile(file);
        runStaging(file);
    }, [stagedFile, t, runStaging]);

    const handleRejectedFile = useCallback((file: File, errorMessage: string) => {
        // Figma 03/03 and 03/04: stage the rejected file as an error card
        // instead of a drop-zone message. Do not start staging.
        if (stagedFile) {
            setDropFieldError(t('BULK_SUBSCRIPTION.SINGLE_FILE_ONLY_ERROR'));
            return;
        }
        abortInFlightStaging();
        setDropFieldError(undefined);
        setStagedUploadId(undefined);
        setStagedFile(file);
        setUploadItem({
            id: file.name,
            file,
            status: 'error',
            errorMessage,
        });
    }, [abortInFlightStaging, stagedFile, t]);

    const clearStagedState = useCallback((uploadIdToDelete?: number) => {
        abortInFlightStaging();
        if (uploadIdToDelete !== undefined) {
            deleteStagedUpload(uploadIdToDelete);
        }
        setStagedFile(undefined);
        setStagedUploadId(undefined);
        setUploadItem(undefined);
        setDropFieldError(undefined);
    }, [abortInFlightStaging, deleteStagedUpload]);

    const handleFileRemove = useCallback(() => {
        // Removal during the server-side import is blocked — clearing
        // state mid-flight would leave the POST racing back with nothing
        // to update. The trash / X button is hidden in that state anyway;
        // this is just a belt-and-braces guard.
        if (isImporting) {
            return;
        }
        clearStagedState(stagedUploadId);
    }, [isImporting, clearStagedState, stagedUploadId]);

    const runImport = useCallback((file: File, uploadId: number) => {
        // Flip the row back to a healthy "ready" state so the user
        // doesn't see a stale error row while the retry POST is in
        // flight. The button's own `loading={isImporting}` spinner is
        // the primary progress affordance for this step (the import
        // POST has no body worth streaming a progress bar for).
        setUploadItem({
            id: file.name,
            file,
            status: 'success',
            progress: 100,
        });
        importStagedFile({
            entityId: entity.id,
            uploadId,
            action,
        });
    }, [entity.id, action, importStagedFile]);

    const handleFileRetry = useCallback(() => {
        if (!stagedFile) {
            return;
        }
        const validationError = validateFile(stagedFile, {
            acceptedFileTypes: ACCEPTED_FILE_TYPES,
            maxFileSize: MAX_FILE_SIZE,
        });
        if (validationError) {
            const errorMessage = validationError.code === 'file-too-large'
                ? t('UPLOADER.FILE_TOO_LARGE', { size: formatBytes(MAX_FILE_SIZE) })
                : t('UPLOADER.FILE_TYPE_INVALID', { types: formatAcceptedFileTypes(ACCEPTED_FILE_TYPES) });
            setUploadItem({
                id: stagedFile.name,
                file: stagedFile,
                status: 'error',
                errorMessage,
            });
            return;
        }
        // If staging already succeeded, only the import step failed
        // — re-run that step alone so we don't re-upload the same bytes.
        // Otherwise, replay the whole flow starting from `POST /uploads`.
        if (stagedUploadId !== undefined) {
            runImport(stagedFile, stagedUploadId);
            return;
        }
        runStaging(stagedFile);
    }, [stagedFile, stagedUploadId, runImport, runStaging, t]);

    const isReadyToImport =
        !!stagedFile && stagedUploadId !== undefined && uploadItem?.status === 'success' && !isImporting;

    const onStartImportClick = () => {
        if (isImporting || isStaging) {
            return;
        }
        // Figma Frame 03/01: the button stays enabled so this click can
        // surface the drop-zone error instead of being a no-op.
        if (!stagedFile) {
            setDropFieldError(t('BULK_SUBSCRIPTION.NO_FILE_SELECTED_ERROR'));
            return;
        }
        const validationError = validateFile(stagedFile, {
            acceptedFileTypes: ACCEPTED_FILE_TYPES,
            maxFileSize: MAX_FILE_SIZE,
        });
        if (validationError) {
            const errorMessage = validationError.code === 'file-too-large'
                ? t('UPLOADER.FILE_TOO_LARGE', { size: formatBytes(MAX_FILE_SIZE) })
                : t('UPLOADER.FILE_TYPE_INVALID', { types: formatAcceptedFileTypes(ACCEPTED_FILE_TYPES) });
            setDropFieldError(undefined);
            setUploadItem({
                id: stagedFile.name,
                file: stagedFile,
                status: 'error',
                errorMessage,
            });
            return;
        }
        if (!isReadyToImport || stagedUploadId === undefined) {
            // Staging failed or the card is still in an error state
            // (missing rights / technical). The file-card error stays visible.
            return;
        }
        runImport(stagedFile, stagedUploadId);
    };

    const discardAndClose = () => {
        clearStagedState(stagedUploadId);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={() => !isImporting && discardAndClose()}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault>
                        {t('BULK_SUBSCRIPTION.IMPORT_FILE')}
                    </DialogHeaderDefault>
                </DialogHeader>

                {/*
                  Figma frame `5887:29535 / 01 Import file Dialog` places
                  the Uploader directly under the header and adds a
                  bordered Options block below it. The DialogBody owns no
                  padding — each block ships its own internal padding.
                */}
                <DialogBody padding="none" className="tw-flex tw-flex-col">
                    <Uploader
                        acceptedFileTypes={ACCEPTED_FILE_TYPES}
                        maxFileSize={MAX_FILE_SIZE}
                        uploads={uploadItem ? [uploadItem] : []}
                        isDisabled={isImporting}
                        errorText={dropFieldError}
                        onFilesAdded={handleFilesAdded}
                        onFileRemove={handleFileRemove}
                        onFileRetry={handleFileRetry}
                        onRejectedFile={handleRejectedFile}
                    />

                    <RadioGroup
                        value={action}
                        onValueChange={(value) => setAction(value as BulkSubscriptionImportAction)}
                        disabled={isImporting}
                        className="tw-flex tw-flex-col tw-gap-1 tw-px-5 tw-py-3 lg:tw-px-8 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-tertiary"
                    >
                        {IMPORT_OPTIONS.map(option => {
                            const inputId = `bulk-sub-import-option-${option.action}`;
                            return (
                                <div key={option.action} className="tw-flex tw-items-center tw-gap-2">
                                    <RadioGroupItem value={option.action} id={inputId} />
                                    <label
                                        htmlFor={inputId}
                                        className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-primary tw-whitespace-nowrap tw-m-0"
                                    >
                                        {t(option.labelKey)}
                                    </label>
                                    <Tooltip popupContent={t(option.tooltipKey)} size="sm" />
                                </div>
                            );
                        })}
                    </RadioGroup>
                </DialogBody>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="default"
                        loading={isImporting}
                        onClick={onStartImportClick}
                    >
                        {t('BULK_SUBSCRIPTION.START_IMPORT')}
                    </Button>
                    <DialogClose asChild={true}>
                        <Button variant="secondary" disabled={isImporting} onClick={discardAndClose}>
                            {t('CANCEL')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/**
 * Maps a server error to one of the three Figma-defined bulk-subscription
 * upload error strings (Frame 03/05, 03/06). We keep the exact server
 * message as the fallback because a specific business error (e.g. an
 * invalid workbook layout) is often more informative than the generic
 * "technical error" copy.
 */
function resolveUploadErrorMessage(
    error: unknown,
    t: (key: string, values?: Record<string, string | number>) => string,
): string {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
            return t('BULK_SUBSCRIPTION.MISSING_UPLOAD_RIGHTS');
        }
        if (status === 406) {
            return t('BULK_SUBSCRIPTION.FILE_INFECTED');
        }
        if (status === 422) {
            return t('BULK_SUBSCRIPTION.VIRUS_SCAN_PENDING');
        }
        const serverMessage = (error.response?.data as { message?: string } | undefined)?.message;
        if (serverMessage) {
            return serverMessage;
        }
    }

    return t('BULK_SUBSCRIPTION.TECHNICAL_ERROR');
}

export { UploadNewEntriesDialog };
